import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';

// Nach dem Versand ist die Bestätigung erst „ausstehend“ – zugestellt oder nicht zugestellt ist sie, wenn die
// Nachrichtenverfolgung von Microsoft (bzw. eine Unzustellbarkeitsmeldung) es meldet (OFFENE_FRAGEN D-25).
const stateText: Record<string, string> = {
  SENT: 'Gesendet (vor Einführung der Zustellprüfung)',
  QUEUED: 'Wird gesendet …',
  PENDING: 'Versendet – Zustellung wird geprüft …',
  DELIVERED: 'Zugestellt',
  FAILED: 'Nicht zugestellt',
  UNKNOWN: 'Zustellung unklar – keine Bestätigung von Microsoft',
  OFF: 'Aus – wird nicht automatisch gesendet',
  NO_EMAIL: 'Keine E-Mail-Adresse beim Eigentümer',
  UNDELIVERABLE: 'E-Mail-Adresse als unzustellbar gemeldet – bitte im Lead korrigieren, bis dahin keine Bestätigung',
  WAITING_FIELD_SALES: 'Wird gesendet, sobald ein Makler zugewiesen ist',
  PROFILE_INCOMPLETE: 'Im Maklerprofil fehlen Telefon, E-Mail oder Anschrift – bitte ergänzen, dann „Jetzt senden“',
  NOT_SENT: 'Noch nicht gesendet',
  UNAVAILABLE: 'E-Mail-Versand nicht eingerichtet',
};

// ConfirmationCard: Terminbestätigung per E-Mail an den Eigentümer (bei Besichtigungen an den Interessenten) – Stand,
// jetzt (erneut) senden, Automatik aus/an.
export function ConfirmationCard({ appointmentId, viewing = false, compact = false }: { appointmentId: string; viewing?: boolean; compact?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const key = projectKeys.confirmation(project.id, appointmentId);
  const status = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => papi.appointmentConfirmation(appointmentId, signal),
    refetchInterval: (q) => (q.state.data?.state === 'QUEUED' ? 5_000 : q.state.data?.state === 'PENDING' ? 60_000 : false),
  });
  // Kopie an den Makler (Dominik 13.10.2026: „Terminbestätigung senden … an Manfred Kopie“).
  const [copy, setCopy] = useState(false);
  const act = useMutation({
    mutationFn: (action: 'send' | 'off' | 'auto') => papi.updateAppointmentConfirmation(appointmentId, action, action === 'send' && copy),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });
  const canAct = can('appointment.edit') || can('appointment.create');
  const s = status.data;
  if (compact) {
    if (!s || s.state === 'UNAVAILABLE') return null;
    return (
      <p className="muted confirmation-line">
        {viewing ? 'Besichtigungsbestätigung an den Interessenten' : 'Terminbestätigung an den Eigentümer'}:{' '}
        {viewing && s.state === 'NO_EMAIL' ? 'Keine E-Mail-Adresse beim Interessenten' : (stateText[s.state] ?? s.state)}
        {s.state === 'DELIVERED' && s.delivered_at ? ` am ${fmtDateTime(s.delivered_at)}` : ''}
      </p>
    );
  }
  return (
    <section className="card" aria-labelledby="confirmation-title">
      <h2 id="confirmation-title">{viewing ? 'Besichtigungsbestätigung an den Interessenten' : 'Terminbestätigung an den Eigentümer'}</h2>
      {status.isPending ? (
        <p className="muted">Wird geladen …</p>
      ) : status.isError ? (
        <ErrorMessage error={status.error} onRetry={() => void status.refetch()} />
      ) : (
        s && (
          <>
            <p>
              <strong className={s.state === 'FAILED' || s.state === 'UNDELIVERABLE' ? 'confirmation-failed' : s.state === 'DELIVERED' ? 'confirmation-ok' : undefined}>
                {viewing && s.state === 'NO_EMAIL' ? 'Keine E-Mail-Adresse beim Interessenten' : (stateText[s.state] ?? s.state)}
              </strong>
              {s.state === 'DELIVERED' && s.delivered_at ? ` am ${fmtDateTime(s.delivered_at)}` : ''}
              {(s.state === 'SENT' || s.state === 'PENDING') && s.sent_at ? ` · versendet am ${fmtDateTime(s.sent_at)}` : ''}
              {s.to ? ` · an ${s.to}` : ''}
            </p>
            {s.detail && <p className={s.state === 'FAILED' ? 'confirmation-failed' : 'muted'}>{s.detail}</p>}
            {s.outdated && ['SENT', 'PENDING', 'DELIVERED'].includes(s.state) && <p className="muted">Der Termin wurde danach geändert – die neue Bestätigung folgt automatisch.</p>}
            <p className="muted">
              Text der Vorlage „{viewing ? 'Besichtigungsbestätigung' : 'Terminbestätigung'}“ mit Datenschutzinformation. Sie erscheint in der Historie des
              Leads.
            </p>
            {canAct && s.state !== 'UNAVAILABLE' && (
              <div className="form-actions">
                {s.to && s.state !== 'UNDELIVERABLE' && (
                  <>
                    <button type="button" className="btn btn-small" disabled={act.isPending} onClick={() => act.mutate('send')}>
                      {['SENT', 'PENDING', 'DELIVERED', 'FAILED', 'UNKNOWN'].includes(s.state) ? 'Erneut senden' : 'Jetzt senden'}
                    </button>
                    {!viewing && (
                      <label className="checkbox">
                        <input type="checkbox" checked={copy} onChange={(e) => setCopy(e.target.checked)} /> Kopie an den Makler
                      </label>
                    )}
                  </>
                )}
                {s.mode === 'AUTO' ? (
                  <button type="button" className="btn btn-small btn-ghost" disabled={act.isPending} onClick={() => act.mutate('off')}>
                    Automatik aus
                  </button>
                ) : (
                  <button type="button" className="btn btn-small btn-ghost" disabled={act.isPending} onClick={() => act.mutate('auto')}>
                    Automatik an
                  </button>
                )}
              </div>
            )}
            {act.isError && <MutationError error={act.error} />}
          </>
        )
      )}
    </section>
  );
}
